import React from "react";
import { createPortal } from "react-dom";

interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: React.ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  isConfirming?: boolean;
  confirmDisabled?: boolean;
  /** Red confirm button for destructive actions (delete, etc.). Defaults to brand green. */
  destructive?: boolean;
  /** Visual style. "light" (default) is the white card; "dark" is the borderless dark card. */
  variant?: "light" | "dark";
  errorMessage?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  title,
  message,
  confirmLabel,
  cancelLabel = "Cancel",
  isConfirming = false,
  confirmDisabled = false,
  destructive = false,
  variant = "light",
  errorMessage,
  onConfirm,
  onCancel,
}) => {
  if (!isOpen) return null;

  const dark = variant === "dark";

  const cardClass = dark
    ? "bg-[#2b2b2d] text-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden"
    : "bg-white rounded-xl shadow-xl max-w-md w-full overflow-hidden";
  const titleClass = dark ? "text-white" : "text-gray-900";
  const messageClass = dark ? "text-gray-100" : "text-gray-700";

  const confirmColor = destructive
    ? "bg-theme-danger hover:opacity-90"
    : "bg-theme-primary hover:bg-theme-primary-hover";

  const cancelClass = dark
    ? "px-4 py-2 rounded-lg text-gray-300 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
    : "px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed";

  // Portal to <body> so the fixed overlay covers the full viewport even when an
  // ancestor (e.g. the sidebar) has a transform/filter that would otherwise trap it.
  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className={`fixed inset-0 z-[10000] flex items-center justify-center p-4 ${
        dark ? "bg-black/70 backdrop-blur-sm" : "bg-black/50"
      }`}
      onClick={(e) => {
        if (e.currentTarget === e.target && !isConfirming) onCancel();
      }}
      role="dialog"
      aria-modal="true"
    >
      <div className={cardClass} onClick={(e) => e.stopPropagation()}>
        <div className={`flex items-start justify-between gap-3 px-6 pt-5 ${dark ? "" : "pb-4 border-b border-gray-100"}`}>
          <h3 className={`text-lg font-semibold ${titleClass}`}>{title}</h3>

          {!dark && (
            <button
              type="button"
              onClick={() => {
                if (!isConfirming) onCancel();
              }}
              className="text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Close"
              disabled={isConfirming}
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        <div className={`px-6 ${dark ? "pt-2" : "py-4"} text-sm ${messageClass}`}>{message}</div>

        {errorMessage && (
          <div className={`px-6 pb-2 text-sm ${dark ? "text-red-300" : "text-red-500"}`} role="alert">
            {errorMessage}
          </div>
        )}

        <div className={`flex items-center justify-end gap-3 px-6 py-4 ${dark ? "" : "border-t border-gray-100"}`}>
          <button
            type="button"
            onClick={() => {
              if (!isConfirming) onCancel();
            }}
            disabled={isConfirming}
            className={cancelClass}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isConfirming || confirmDisabled}
            className={`px-4 py-2 rounded-lg text-white ${confirmColor} transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2`}
          >
            {isConfirming ? (
              <>
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8z" />
                </svg>
                {confirmLabel}
              </>
            ) : (
              confirmLabel
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ConfirmDialog;
